import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useDemoMode } from '@/features/demo/useDemoMode';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { api } from '@/lib/api';
import './RemoveDemoDataButton.css';

export function RemoveDemoDataButton() {
  const { loaded, available, refetch } = useDemoMode();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeKey(() => setIsOpen(false), isOpen);
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] =
    useModalOverlayClick(() => setIsOpen(false), 30);

  const handleConfirm = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.http.post('/demo-seed/purge', {});
      queryClient.invalidateQueries({
        predicate: (q) => {
          const k = JSON.stringify(q.queryKey);
          return (
            k.includes('email') || k.includes('gmail') ||
            k.includes('integration') ||
            k.includes('object') ||
            k.includes('module')
          );
        },
      });
      window.dispatchEvent(new CustomEvent('xavior:demo-data-loaded'));
      await refetch();
      setIsOpen(false);
    } catch (err: any) {
      if (err?.status === 401) {
        setError('Your session expired. Reload the page and sign in, then try again.');
        return;
      }
      console.error('[RemoveDemoDataButton] purge failed:', err);
      setError('Could not remove demo data. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [busy, queryClient, refetch]);

  if (!available || !loaded) return null;

  return (
    <>
      <button
        type="button"
        className="remove-demo-btn"
        onClick={() => setIsOpen(true)}
        title="Remove demo data from this workspace"
      >
        Remove demo data
      </button>
      {isOpen && createPortal(
        <div
          className="remove-demo-modal-overlay"
          onMouseDown={handleOverlayMouseDown}
          onClick={handleOverlayClick}
        >
          <div
            ref={modalContentRef}
            className="remove-demo-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="remove-demo-modal-header">
              <h2>Remove demo data?</h2>
              <button
                className="remove-demo-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
                disabled={busy}
              >
                &times;
              </button>
            </div>
            <div className="remove-demo-modal-body">
              <p>
                This deletes the demo dataset (sample emails, events, tasks, contacts, places,
                notes, and pages) from this workspace. Your own data is untouched.
              </p>
              <p className="remove-demo-modal-hint">
                You can add demo data back anytime from the <strong>workspace dropdown</strong> in
                the sidebar.
              </p>
              {error && <p className="remove-demo-modal-error">{error}</p>}
            </div>
            <div className="remove-demo-modal-actions">
              <button
                type="button"
                className="remove-demo-modal-cancel"
                onClick={() => setIsOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="remove-demo-modal-confirm"
                onClick={handleConfirm}
                disabled={busy}
              >
                {busy ? 'Removing…' : 'OK, remove'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
